import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { CategoryService } from './category.service';
import { ApiService } from './api.service';
import { MycontentserviceService } from './mycontentservice.service';
import { URLS } from '../constants/URLS';

/**
 * Holds the in-memory shorts feed for the YT-style player.
 *
 * Two feed origins share this state:
 *   - category-bound  (sectionId, paged via sectionsByCategoryAndChannelId)
 *   - series-bound    (seriesId, bounded list of episodes via getEpisodicConent)
 * The player UI is identical for both — only the source/pagination behavior
 * differs. `noMore` is `true` from the start for series feeds since the
 * episode list is finite and fully fetched in one call.
 *
 * Why a service: when a user clicks a Short in category-movie or percategory,
 * the page already has the section's items loaded — re-fetching on player
 * mount would be wasteful and flash. movie-thumbnail calls `seed()` /
 * `seedSeries()` before navigation, and ShortsPlayerComponent reads via
 * `state$`.
 *
 * URL is still the source of truth: if the player loads with a categoryId
 * or seriesId query param but the service is cold (refresh / shared link),
 * the player triggers `loadInitialPage(categoryId)` or
 * `loadInitialSeriesEpisodes(seriesId)` itself.
 *
 * Pagination (category only) uses the same endpoint as percategory:
 *   CategoryService.sectionsByCategoryAndChannelId(categoryId, startIndex, lastIndex)
 * 10 items per page, append on scroll-near-end, stop when API returns empty.
 */
@Injectable({ providedIn: 'root' })
export class ShortsFeedService {
  private readonly PAGE_SIZE = 10;

  // Single source of truth the player subscribes to.
  private state = new BehaviorSubject<{
    items: any[];
    categoryId: string | null;
    seriesId: string | number | null;
    startingShortId: string | number | null;
    noMore: boolean;
    loading: boolean;
  }>({
    items: [],
    categoryId: null,
    seriesId: null,
    startingShortId: null,
    noMore: false,
    loading: false
  });
  state$ = this.state.asObservable();

  constructor(
    private categoryService: CategoryService,
    private apiService: ApiService,
    private mycontentService: MycontentserviceService
  ) {}

  /**
   * Called from movie-thumbnail before navigating into the player.
   * Avoids the first-page refetch when context is already loaded.
   */
  seed(categoryId: string | null, items: any[], startingShortId: string | number | null): void {
    this.state.next({
      items: items ? [...items] : [],
      categoryId,
      seriesId: null,
      startingShortId,
      noMore: false,
      loading: false
    });
  }

  /**
   * Series-bound warm seed. Used by movie-thumbnail when a short-drama
   * episode is tapped: we seed with at least the tapped item so the first
   * slide renders instantly, then the player calls
   * `loadInitialSeriesEpisodes` to fill the rest of the episode list.
   *
   * `noMore` stays false until the cold-fetch completes — flipping it to
   * true here would prevent the fill-in fetch from running.
   */
  seedSeries(seriesId: string | number, items: any[], startingShortId: string | number | null): void {
    this.state.next({
      items: items ? [...items] : [],
      categoryId: null,
      seriesId,
      startingShortId,
      noMore: false,
      loading: false
    });
  }

  /**
   * Player calls this on init when service is cold (refresh / deep link with
   * ?categoryId but no seeded items). Fetches page 0.
   */
  loadInitialPage(categoryId: string, startingShortId: string | number | null): Observable<any[]> {
    this.state.next({ ...this.state.getValue(), categoryId, seriesId: null, startingShortId, loading: true });
    return this.categoryService
      .sectionsByCategoryAndChannelId(categoryId, '0', String(this.PAGE_SIZE))
      .pipe(
        map((items: any[]) => Array.isArray(items) ? items : []),
        tap(items => {
          this.state.next({
            items,
            categoryId,
            seriesId: null,
            startingShortId,
            noMore: items.length < this.PAGE_SIZE,
            loading: false
          });
        }),
        catchError(err => {
          this.state.next({ ...this.state.getValue(), loading: false });
          return of([]);
        })
      );
  }

  /**
   * Series cold-fetch: hits the same episodic endpoint as shaka-player's
   * `getSeriesData`, flattens seasons → episodes preserving order, runs
   * each tqrVideoBean through `transformData` so the payload shape matches
   * what the carousel/player expect, and replaces state.
   *
   * Always sets noMore: true — episode lists are bounded, so the player's
   * near-end pagination trigger is a no-op for series feeds.
   *
   * Called by the player on cold start (refresh / deep link with ?seriesId)
   * and also as a "fill" pass after `seedSeries` to top up the rest of the
   * episodes beyond the initial tapped one.
   */
  loadInitialSeriesEpisodes(seriesId: string | number, startingShortId: string | number | null): Observable<any[]> {
    this.state.next({ ...this.state.getValue(), categoryId: null, seriesId, startingShortId, loading: true });
    return this.apiService
      .post(`${URLS.getEpisodicConent}?series_id=${seriesId}`, {})
      .pipe(
        map((result: any) => {
          // The episodic endpoint envelopes the payload as
          // { status, message, data: { ..., seasons: [...] } }, same as
          // shaka-player consumes. Fallback to root-level seasons just in
          // case a deployment ships the data unwrapped.
          const seasons = result?.data?.seasons ?? result?.seasons ?? [];
          const episodes: any[] = [];
          for (const season of seasons) {
            for (const ep of (season.episodes ?? [])) {
              const bean = ep?.tqrVideoBean;
              if (!bean) continue;
              // transformData mutates in place and returns void, so call
              // for side-effects then push the (now-annotated) bean.
              this.mycontentService.transformData(bean);
              episodes.push(bean);
            }
          }
          return episodes;
        }),
        tap(items => {
          this.state.next({
            items,
            categoryId: null,
            seriesId,
            startingShortId,
            noMore: true,
            loading: false
          });
        }),
        catchError(err => {
          this.state.next({ ...this.state.getValue(), loading: false });
          return of([]);
        })
      );
  }

  /**
   * Called by the player when current index nears the end of the loaded feed.
   * Appends next page. Sets noMore=true when backend returns fewer than
   * PAGE_SIZE items (end of section).
   */
  loadNextPage(): Observable<any[]> {
    const current = this.state.getValue();
    if (current.loading || current.noMore || !current.categoryId) return of([]);

    const startIndex = current.items.length;
    this.state.next({ ...current, loading: true });

    return this.categoryService
      .sectionsByCategoryAndChannelId(
        current.categoryId,
        String(startIndex),
        String(startIndex + this.PAGE_SIZE)
      )
      .pipe(
        map((items: any[]) => Array.isArray(items) ? items : []),
        tap(items => {
          const latest = this.state.getValue();
          // De-dupe by vidLibId so accidental overlap from server doesn't double up.
          const existingIds = new Set(latest.items.map(i => i.vidLibId));
          const newOnes = items.filter(i => !existingIds.has(i.vidLibId));
          this.state.next({
            items: [...latest.items, ...newOnes],
            categoryId: latest.categoryId,
            seriesId: latest.seriesId,
            startingShortId: latest.startingShortId,
            noMore: items.length < this.PAGE_SIZE,
            loading: false
          });
        }),
        catchError(err => {
          this.state.next({ ...this.state.getValue(), loading: false });
          return of([]);
        })
      );
  }

  /**
   * Single-short mode for entry points without section context (search,
   * watchlist, more-like-this).
   */
  seedSingle(short: any): void {
    this.state.next({
      items: short ? [short] : [],
      categoryId: null,
      seriesId: null,
      startingShortId: short?.vidLibId ?? null,
      noMore: true,
      loading: false
    });
  }

  getSnapshot() {
    return this.state.getValue();
  }

  clear(): void {
    this.state.next({
      items: [],
      categoryId: null,
      seriesId: null,
      startingShortId: null,
      noMore: false,
      loading: false
    });
  }
}
